import { MusicNoteIdentity } from "../../wasm/pkg/wasm";
import { StringsTuning, TablatureSingleChord } from "../components/instruments/InstrumentTypes.model";
import { ChordInScaleEdit } from "../models/wasm.model";

type TabGenerationArgs = {
  chord: ChordInScaleEdit;
  capoPosition: number;
  stringsTuning: StringsTuning;
};

type TabCandidateNote = { noteId: MusicNoteIdentity | "x"; noteIdx: number | "x" };

/**

  Rules for building arrangements from notes:
  (1) each note must occur at least once if possible
     ...this is kinda hard to do, fuck.  TODO: start here at the hard part in the morning. Hooray!
  
  a. Iterate over the set of requirements until the score is maximized?
  b. Choose the leftmost values - increment each line to try to meet requirements better?
  c. Generate all permutations - see which one has the highest score? <-- this is probably easiest/dumbest.  With only 4 frets, should be 2 max 3 notes per string
  
  
  (2) Prioritization order for repeat notes: open string, I, III, V, VI, ...

  Rules for picking the best arrangement:
  (0) bake in these statistics to the builds
  (1) In which of these arrangements can I find the most notes on individual strings?
  (2) In which of these arrangements is the highest degree note on one of the highest strings?

  Procedure:
  (1) pick best arrangement from each area of fretboard
  (2) pick best of best
 */
export function chooseBestStringInstrumentTab({ chord, capoPosition, stringsTuning }: TabGenerationArgs): TablatureSingleChord {
  const { stringsLowToHigh } = stringsTuning;
  const chordNoteIds = chord.notes.map((n) => n.note_id);

  // Algorithm1: build upon the bass note

  // 1.1: Find the bass note on the lowest two strings;

  const lowestStringBassNoteIdx = stringsLowToHigh[0].findIndex((nid, idx) => idx >= capoPosition && nid === chord.bass_note_id);
  const nextLowestStrBassNoteIdx = stringsLowToHigh[1].findIndex((nid, idx) => idx >= capoPosition && nid === chord.bass_note_id);
  let bassStringIndex;
  let bassStringNoteIndex;
  if (lowestStringBassNoteIdx !== -1 && lowestStringBassNoteIdx <= nextLowestStrBassNoteIdx) {
    bassStringIndex = 0;
    bassStringNoteIndex = lowestStringBassNoteIdx;
  } else if (nextLowestStrBassNoteIdx !== -1) {
    bassStringIndex = 1;
    bassStringNoteIndex = nextLowestStrBassNoteIdx;
  } else {
    // algo 1 has failed us, oh no.  FIXME
    bassStringIndex = 0;
    bassStringNoteIndex = 0;
  }

  // 1.2. Find valid indices for the remaining strings

  const validUpperStringNoteIndices: Array<Array<{ noteIdx: number; noteId: MusicNoteIdentity }>> = stringsLowToHigh
    .slice(bassStringIndex + 1)
    .map((str: Array<MusicNoteIdentity>) => {
      return str
        .map((noteId, noteIdx) => ({ noteId, noteIdx }))
        .filter(({ noteId, noteIdx }) => noteIdx >= capoPosition && chordNoteIds.includes(noteId));
    });

  const bassStrings: Array<Array<TabCandidateNote>> = bassStringIndex === 0 ? [] : [[{ noteIdx: "x", noteId: "x" }]];
  bassStrings.push([{ noteIdx: bassStringNoteIndex, noteId: chord.bass_note_id }]);

  // console.log("bassStrings", bassStrings);

  // Left oriented: can I find all the notes within 3 to the left?
  const validLeftOrientedNI = bassStrings.concat(
    validUpperStringNoteIndices.map((arr) =>
      arr.filter(({ noteIdx }) => {
        return noteIdx === capoPosition || (noteIdx >= bassStringNoteIndex - 3 && noteIdx <= bassStringNoteIndex);
      })
    )
  );

  // Middle-left oriented: can I find all the notes within 2 left, 1 right?
  const validMidLeftOrientedNI = bassStrings.concat(
    validUpperStringNoteIndices.map((arr) =>
      arr.filter(({ noteIdx }) => {
        return noteIdx === capoPosition || (noteIdx >= bassStringNoteIndex - 2 && noteIdx <= bassStringNoteIndex + 1);
      })
    )
  );

  // Middle-right oriented: can I find all the notes within 1 left, 2 right?
  const validMidRightOrientedNI = bassStrings.concat(
    validUpperStringNoteIndices.map((arr) =>
      arr.filter(({ noteIdx }) => {
        return noteIdx === capoPosition || (noteIdx >= bassStringNoteIndex - 1 && noteIdx <= bassStringNoteIndex + 2);
      })
    )
  );

  // Right oriented: can I find all the notes within 3 to the right?
  const validRightOrientedNI = bassStrings.concat(
    validUpperStringNoteIndices.map((arr) =>
      arr.filter(({ noteIdx }) => {
        return noteIdx === capoPosition || (noteIdx >= bassStringNoteIndex && noteIdx <= bassStringNoteIndex + 3);
      })
    )
  );

  let contenders: Array<TabCandidate> = [];
  let highestScore: TabCandidateScore = 0;

  [validLeftOrientedNI, validMidLeftOrientedNI, validMidRightOrientedNI, validRightOrientedNI].forEach((allowedFrets) => {
    const x = generateBestFrettingPermutations(allowedFrets, chordNoteIds, capoPosition);
    const which = chooseBetterCandidateScore(x.highestScore, highestScore);
    if (which === "left") {
      highestScore = x.highestScore;
      contenders = x.candidates; // throw away lower score(s)
    }
  });

  contenders.sort((a, b) => {
    // abuse sorting builtin with jank
    const which = chooseBetterCandidateScore(a.score, b.score);
    if (which === "left") {
      return -1;
    } else {
      return 1;
    }
  });

  // console.log("top contenders", contenders.slice(0, 3));

  return {
    stringFretsLowToHigh: contenders[0].lowToHigh.map((tcn) => tcn.noteIdx),
  };
}
type TabCandidateScore =
  | 0
  | {
      distinctNoteCount: number;
      numOpenStrings: number;
      numSilencedStrings: number;
      isHighestNoteOnHighStrings: boolean;
    };
type TabCandidate = {
  lowToHigh: Array<TabCandidateNote>;
  score: TabCandidateScore;
};

function chooseBetterCandidateScore(a: TabCandidateScore, b?: TabCandidateScore): "left" | "right" {
  if (!b) {
    return "left";
  } else if (a === 0) {
    return "right";
  } else if (a.distinctNoteCount < b.distinctNoteCount) {
    return "right";
  } else if (a.distinctNoteCount > b.distinctNoteCount) {
    return "left";
  } else if (a.numOpenStrings > b.numOpenStrings) {
    return "left";
  } else if (a.numOpenStrings < b.numOpenStrings) {
    return "right";
  } else if (a.numSilencedStrings < b.numSilencedStrings) {
    return "left";
  } else if (a.numSilencedStrings > b.numSilencedStrings) {
    return "right";
  } else if (a.isHighestNoteOnHighStrings) {
    return "left";
  }
  return "right";
}

function chooseBetterCandidate(a: TabCandidate, b?: TabCandidate): { which: "left" | "right"; choice: TabCandidate } {
  const which = chooseBetterCandidateScore(a.score, b?.score);
  if (which === "left") {
    return { which, choice: a };
  }
  return { which, choice: b! };
}

// function scoreToPoints(a: TabCandidate): number {
//   if (a.score === 0) return 0;
//   return (
//     a.score.distinctNoteCount * 100 +
//     (a.score.numOpenStrings - a.score.numSilencedStrings) * 10 +
//     (a.score.isHighestNoteOnHighStrings ? 1 : 0)
//   );
// }

function copyTabCandidateAdjustString(tc: TabCandidate, strIndex: number, note: TabCandidateNote) {
  const copy = { ...tc, lowtoHigh: [...tc.lowToHigh] };
  copy.lowToHigh[strIndex] = note;
  return copy;
}

function generateBestFrettingPermutations(
  possibleNotesPerString: Array<Array<TabCandidateNote>>,
  chordNoteIds: Array<MusicNoteIdentity>,
  capoPosition: number
): { candidates: Array<TabCandidate>; highestScore: TabCandidateScore } {
  const candidates: Array<TabCandidate> = [];
  let highestScoreCandidate: TabCandidate | undefined = undefined;

  const scoreAndAddCandidate = (unscored: TabCandidate) => {
    const cand = calculateTabCandidateScore(unscored, chordNoteIds, capoPosition);
    const which = chooseBetterCandidateScore(cand.score, highestScoreCandidate?.score);
    if (which === "left") {
      candidates.push(cand); // filter: no point adding candidates which suck
      highestScoreCandidate = cand;
    }
  };

  const leftMost: TabCandidate = {
    lowToHigh: possibleNotesPerString.map((n) => {
      return n[0] || { noteId: "x", noteIdx: "x" };
    }),
    score: 0,
  };
  scoreAndAddCandidate(leftMost);

  // For each string, if that string has 2nd and 3rd options, store those permutations built off the [:0] options
  possibleNotesPerString.forEach((stringCandidateFrets, stringIndex) => {
    stringCandidateFrets.forEach((fret, fretIndex) => {
      if (fretIndex > 0) {
        const next = copyTabCandidateAdjustString(leftMost, stringIndex, fret);
        scoreAndAddCandidate(next);
      }
    });
  });

  // The second leftmost...
  const nextLeftMost: TabCandidate = {
    lowToHigh: possibleNotesPerString.map((n) => {
      return n[1] || n[0] || { noteId: "x", noteIdx: "x" };
    }),
    score: 0,
  };
  scoreAndAddCandidate(nextLeftMost);

  // For each string, if that string has 3rd options, store those permutations built off the [:1] options.
  // This should be good enough.  It's obviously not as powerful as recursing, but given the constraints of the inputs (and human fingers) probably fine.  we'll see.
  possibleNotesPerString.forEach((stringCandidateFrets, stringIndex) => {
    stringCandidateFrets.forEach((fret, fretIndex) => {
      if (fretIndex > 1) {
        const next = copyTabCandidateAdjustString(nextLeftMost, stringIndex, fret);
        scoreAndAddCandidate(next);
      }
    });
  });

  return { candidates, highestScore: highestScoreCandidate!.score };
}

/**
 * scores:
 *   For each note in the chord: +1 point
 *   highest chord note is on the top two frets: +1 point
 * tiebreakers:
 *   each open string: +1 point
 */
function calculateTabCandidateScore(cand: TabCandidate, chordNoteIds: Array<MusicNoteIdentity>, capoPosition: number): TabCandidate {
  let noteMap: Partial<Record<MusicNoteIdentity, boolean>> = {};
  let distinctNoteCount = 0;
  let numOpenStrings = 0;
  let numSilencedStrings = 0;
  cand.lowToHigh.forEach((tcn) => {
    if (tcn.noteId === "x") {
      numSilencedStrings++;
    } else {
      if (!noteMap[tcn.noteId]) {
        distinctNoteCount++;
        noteMap[tcn.noteId] = true;
      }
      if (tcn.noteIdx === capoPosition) {
        numOpenStrings++;
      }
    }
  });
  // assumption: chord notes are already sorted by interval ascending
  const chordHighestNoteId = chordNoteIds[chordNoteIds.length - 1];
  const isHighestNoteOnHighStrings = cand.lowToHigh.slice(-2).some((tcn) => tcn.noteId === chordHighestNoteId);

  return {
    ...cand,
    score: {
      distinctNoteCount,
      numOpenStrings,
      numSilencedStrings,
      isHighestNoteOnHighStrings,
    },
  };
}
